<template>
	<div class="kova-carousel" :class="[`kova-carousel_theme_${theme}`, { 'kova-carousel_fits': fits }]">
		<div
			ref="track"
			:aria-label="fits ? undefined : label"
			:aria-roledescription="fits ? undefined : 'carousel'"
			class="kova-carousel__track"
			:role="fits ? undefined : 'group'"
			:tabindex="fits ? undefined : 0"
			@scroll.passive="onScroll"
			@scrollend="restoreLoop"
			@touchstart.passive="touching = true"
			@touchend.passive="onTouchEnd"
			@touchcancel.passive="onTouchEnd"
		>
			<slot v-if="loop && !fits" :duplicate="true" />
			<slot :duplicate="false" />
			<slot v-if="loop && !fits" :duplicate="true" />
		</div>

		<div v-show="!fits" class="kova-carousel__controls">
			<!-- Announces the item a dot / swipe landed on (focus stays on the control). -->
			<span aria-live="polite" class="sr-only" role="status" v-text="announcement" />

			<div class="kova-carousel__dots">
				<button
					v-for="index in count"
					:key="index"
					:aria-current="index - 1 === active || undefined"
					:aria-label="`Show ${itemName} ${index} of ${count}`"
					class="kova-carousel__dot"
					:class="{ 'kova-carousel__dot_active': index - 1 === active }"
					type="button"
					@click="goTo(index - 1)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

defineOptions({ name: 'LandingKovaCarousel' })

const props = withDefaults(
	defineProps<{
		count: number
		itemName: string
		label: string
		theme: 'testimonials' | 'providers'
		loop?: boolean
	}>(),
	{ loop: false },
)

const track = ref<HTMLElement | null>(null)
const active = ref(0)
const fits = ref(false)
let resize: ResizeObserver | null = null
let layoutWidth = 0
let scrollEndTimer: ReturnType<typeof setTimeout> | undefined
let touching = false

const announcement = computed(
	() => `${props.itemName.charAt(0).toUpperCase()}${props.itemName.slice(1)} ${active.value + 1} of ${props.count}`,
)
const items = () => Array.from(track.value?.children ?? []) as HTMLElement[]

function onScroll() {
	const el = track.value
	// Resize can dispatch scroll before the observer restores the selected card.
	if (!el || fits.value || el.clientWidth !== layoutWidth) return
	const centre = el.getBoundingClientRect().left + el.clientWidth / 2
	const distances = items().map((item) => {
		const rect = item.getBoundingClientRect()
		return Math.abs(rect.left + rect.width / 2 - centre)
	})
	active.value = distances.indexOf(Math.min(...distances)) % props.count
	// Older browsers without scrollend still settle back into the middle copy.
	clearTimeout(scrollEndTimer)
	scrollEndTimer = setTimeout(restoreLoop, 150)
}

// Adjacent copies keep reviews visible after either end. Rebase only after scrolling
// settles, preserving the exact offset and hiding the duplicate content from screen readers.
function restoreLoop() {
	const el = track.value
	if (!el || !props.loop || fits.value || touching || el.clientWidth !== layoutWidth) return
	const list = items()
	const cycleWidth = list[props.count].offsetLeft - list[0].offsetLeft
	const centre = el.scrollLeft + el.clientWidth / 2
	const start = list[props.count].offsetLeft
	const end = list[props.count * 2].offsetLeft
	if (centre < start || centre >= end) {
		el.scrollBy({ left: centre < start ? cycleWidth : -cycleWidth, behavior: 'instant' })
	}
}

function onTouchEnd() {
	touching = false
	clearTimeout(scrollEndTimer)
	scrollEndTimer = setTimeout(restoreLoop, 150)
}

// CSS owns smooth scrolling and reduced motion; resize explicitly uses an instant jump.
function goTo(index: number, behavior: 'auto' | 'instant' = 'auto') {
	const el = track.value
	const offset = props.loop && !fits.value ? props.count : 0
	const item = items()[offset + Math.min(Math.max(index, 0), props.count - 1)]
	if (!el || !item || fits.value) return
	const rect = item.getBoundingClientRect()
	el.scrollTo({
		left: el.scrollLeft + rect.left - el.getBoundingClientRect().left + (rect.width - el.clientWidth) / 2,
		behavior,
	})
}

function layout() {
	const el = track.value
	const list = items()
	// Font/image loading can change height during a smooth scroll. Do not cancel it.
	if (!el || !list.length || el.clientWidth === layoutWidth) return
	const first = list[0].getBoundingClientRect()
	const last = list[props.count - 1].getBoundingClientRect()
	fits.value = last.right - first.left <= el.clientWidth + 1
	// The multi-card rail opens on its middle card (Figma 6547:17082); a single-card view starts at 0.
	if (!layoutWidth && !fits.value && first.width < el.clientWidth) active.value = Math.floor(props.count / 2)
	void nextTick(() => {
		goTo(active.value, 'instant')
		layoutWidth = el.clientWidth
	})
}

onMounted(() => {
	resize = new ResizeObserver(layout)
	if (track.value) resize.observe(track.value)
})
onBeforeUnmount(() => {
	resize?.disconnect()
	clearTimeout(scrollEndTimer)
})
</script>

<style lang="scss" scoped>
.kova-carousel {
	--dot-size: var(--size-8);
	--dot-step: var(--size-16);
	--slide-width: 100%;

	display: flex;
	flex-direction: column;
	gap: var(--size-16);
	min-width: 0;
}

// Stretch cards to the tallest content so selecting a longer quote/bio does not shift the page.
.kova-carousel__track {
	position: relative;
	display: flex;
	align-items: stretch;
	overflow-x: auto;

	scroll-behavior: smooth;
	scroll-snap-type: x mandatory;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}

	&:focus-visible {
		outline: var(--size-2) solid var(--kova-lp-brand);
		outline-offset: calc(-1 * var(--size-2));
	}

	> :deep(.kova-carousel__slide) {
		flex: 0 0 var(--slide-width);
		scroll-snap-align: center;

		& + .kova-carousel__slide {
			margin-inline-start: var(--size-16);
		}
	}
}

// CSS spacers let the first and last cards reach the centre of the wider rail.
.kova-carousel_theme_testimonials:not(.kova-carousel_fits) .kova-carousel__track {
	&::before,
	&::after {
		content: '';
		flex: 0 0 max(0px, calc((100% - var(--slide-width)) / 2));
	}
}

@media (prefers-reduced-motion: reduce) {
	.kova-carousel__track {
		scroll-behavior: auto;
	}
}

@media (min-width: 1024px) {
	.kova-carousel {
		// Figma 7330:31950: 12px dots, 16px apart, 24px below the cards.
		--dot-size: var(--size-12);
		--dot-step: var(--size-28);

		gap: var(--size-24);
	}

	.kova-carousel_theme_testimonials {
		--slide-width: 400px; // Figma 6557:32371.
	}

	// Figma 6557:32477: Laurie above Theresa, Kimberli above Craig, with staggered rows.
	.kova-carousel_theme_providers .kova-carousel__track {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		grid-template-rows: repeat(3, auto);
		gap: var(--size-16);
		overflow: visible;
		scroll-snap-type: none;

		> :deep(.kova-carousel__slide) {
			align-self: start;
			margin: 0;

			&:nth-child(1) {
				grid-area: 1 / 1 / 3 / 2;
			}
			&:nth-child(2) {
				grid-area: 1 / 2 / 2 / 3;
			}
			&:nth-child(3) {
				grid-area: 3 / 1 / 4 / 2;
			}
			&:nth-child(4) {
				grid-area: 2 / 2 / 4 / 3;
			}
		}
	}
}

// The row is 44px tall for the hit areas while the drawn dots row is 8px: the negative
// block margin keeps the section's rhythm (16px under the track).
.kova-carousel__controls {
	display: flex;
	align-items: center;
	justify-content: center;
	margin: calc(-1 * (var(--size-44) - var(--dot-size)) / 2) 0;
}

.kova-carousel__dots {
	display: flex;
}

// Figma 6415:53541: 8px dots, 8px apart; keep a taller hit area without widening the row.
.kova-carousel__dot {
	position: relative;
	width: var(--dot-step);
	height: var(--size-44);
	padding: 0;

	border: none;
	background: none;
	cursor: pointer;

	&::before {
		content: '';
		position: absolute;
		left: 50%;
		top: 50%;

		width: var(--dot-size);
		height: var(--dot-size);

		border-radius: var(--size-12);
		background: var(--Fill-Default);
		transform: translate(-50%, -50%);
	}
}

.kova-carousel__dot_active::before {
	background: var(--kova-lp-highlight);
}

// Forced colours drop backgrounds; a border keeps the dots (and the active one) visible.
@media (forced-colors: active) {
	.kova-carousel__dot::before {
		width: 0;
		height: 0;
		border: var(--size-4) solid ButtonText;
	}

	.kova-carousel__dot_active::before {
		border-color: Highlight;
	}
}

// Keyboard focus on the dot shows a ring around the whole hit area; the global v2 rule
// (`button:focus-visible`) supplies the colour and offset.
.kova-carousel__dot:focus-visible {
	border-radius: var(--size-44);
}
</style>
